import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpService } from '../../shared/services/http.service';

@Component({
    selector: 'app-template-key-modal',
    templateUrl: './template-key-modal.component.html',
    styleUrls: ['./template-key-modal.component.scss'],
    standalone: false
})
export class TemplateKeyModalComponent implements OnInit {

  @Input() questionId: any;
  @Input() templateKeyId: any = null;
  @Input() label: string = '';

  templateKeys: { id: any, label: string }[] = [];
  filteredTemplateKeys: { id: any, label: string }[] = [];
  keyInputValue: any = '';
  selectedKey: { id: any, label: string } | null = null;
  editedLabel: string = '';
  renaming = false;
  loading = true;
  saving = false;
  errorMessage = '';

  get hasValue(): boolean {
    if (this.selectedKey) {
      return true;
    }
    return typeof this.keyInputValue === 'string' && this.keyInputValue.trim().length > 0;
  }

  get isCreatingNew(): boolean {
    if (this.selectedKey || typeof this.keyInputValue !== 'string') {
      return false;
    }
    const typed = this.keyInputValue.trim();
    if (!typed) {
      return false;
    }
    return !this.templateKeys.some(k => k.label.toLowerCase() === typed.toLowerCase());
  }

  constructor(public activeModal: NgbActiveModal, private httpService: HttpService) { }

  async ngOnInit() {
    const resp: any = await this.httpService.getAsync('feedback/getTemplateKeys');
    if (resp.success) {
      this.templateKeys = resp.data;
      this.filteredTemplateKeys = this.templateKeys;
      this.selectedKey = this.templateKeys.find(k => k.id == this.templateKeyId) || null;
      this.keyInputValue = this.selectedKey;
      this.editedLabel = this.selectedKey ? this.selectedKey.label : (this.label || '');
    } else {
      this.errorMessage = 'Failed to load template keys';
    }
    this.loading = false;
  }

  displayKey = (key: any): string => {
    return key && key.label ? key.label : '';
  }

  onKeySearchChange(value: any) {
    this.keyInputValue = value;
    if (typeof value !== 'string') {
      return;
    }
    // typing diverges from whatever was selected before; re-resolve on save instead.
    this.selectedKey = null;
    this.renaming = false;
    const term = value.toLowerCase().trim();
    this.filteredTemplateKeys = term
      ? this.templateKeys.filter(k => k.label.toLowerCase().includes(term))
      : this.templateKeys;
  }

  onKeySelected(event: any) {
    this.selectedKey = event.option.value;
    this.keyInputValue = this.selectedKey;
    this.editedLabel = this.selectedKey.label;
    this.renaming = false;
    this.filteredTemplateKeys = this.templateKeys;
  }

  startRename() {
    if (!this.selectedKey) {
      return;
    }
    this.editedLabel = this.selectedKey.label;
    this.renaming = true;
  }

  async save() {
    if (!this.hasValue || this.saving) {
      return;
    }
    this.saving = true;
    this.errorMessage = '';

    const typedLabel = typeof this.keyInputValue === 'string' ? this.keyInputValue.trim() : '';

    // typed text matching an existing key's label (without picking it from the panel) reuses that key.
    let key = this.selectedKey
      || this.templateKeys.find(k => k.label.toLowerCase() === typedLabel.toLowerCase())
      || null;

    if (key) {
      const trimmedLabel = this.editedLabel.trim();
      if (trimmedLabel && trimmedLabel !== key.label) {
        const renameResp: any = await this.httpService.postAsync('feedback/renameTemplateKey', {
          id: key.id,
          label: trimmedLabel
        });
        if (!renameResp.success) {
          this.errorMessage = 'Failed to rename template key';
          this.saving = false;
          return;
        }
        key.label = trimmedLabel;
      }
    }

    const payload: any = { id: this.questionId };
    if (key) {
      payload.key = key.id;
    } else {
      payload.label = typedLabel;
    }

    const assignResp: any = await this.httpService.postAsync('feedback/setQuestionTemplateKey', payload);

    this.saving = false;

    if (!assignResp.success) {
      this.errorMessage = 'Failed to assign template key';
      return;
    }

    this.activeModal.close(true);
  }

  cancel() {
    this.activeModal.dismiss();
  }

}
